import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Landing page for the NICE 본인확인 popup.
 *
 * The API Gateway callback receives NICE's POST, records the result, and then
 * 302-redirects the popup here with only an opaque requestId and a coarse
 * status — no personal data ever travels through the browser.
 *
 * This page's whole job is to relay that outcome to the opener and close. It
 * deliberately does NOT call the claim mutation itself: the opener owns the
 * Cognito session and the UI state, and doing it here would leave the opener
 * unaware of the result if the popup closed early.
 */
const IdentityCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('처리 중...');
  // React StrictMode double-invokes effects in development. Without this guard
  // the opener receives the result twice and claims the same requestId twice —
  // the second claim fails as "already used" and would surface an error to the
  // user immediately after a successful verification.
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;

    const s = searchParams.get('status');
    const requestId = searchParams.get('requestId');
    const reason = searchParams.get('reason');

    window.opener?.postMessage(
      {
        type: 'identity-verification-result',
        status: s || 'failed',
        requestId,
        reason,
      },
      window.location.origin,
    );

    if (s === 'success') {
      setStatus('success');
      setMessage('본인인증이 확인되었습니다. 창이 곧 닫힙니다.');
    } else if (s === 'unavailable') {
      setStatus('error');
      setMessage('본인인증 서비스가 아직 준비되지 않았습니다.');
    } else {
      setStatus('error');
      setMessage('본인인증에 실패했습니다. 다시 시도해 주세요.');
    }

    // Close regardless of outcome — the opener renders the error, not the popup.
    const timer = setTimeout(() => window.close(), s === 'success' ? 1200 : 2500);
    return () => clearTimeout(timer);
  }, [searchParams]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center p-8">
        {status === 'processing' && (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D5BFF] mx-auto mb-4" />
        )}
        {status === 'success' && <div className="text-green-600 text-4xl mb-4">✓</div>}
        {status === 'error' && <div className="text-red-600 text-4xl mb-4">✗</div>}
        <p className="text-gray-700">{message}</p>
      </div>
    </div>
  );
};

export default IdentityCallback;
